import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Stage, Layer, Line, Image as KonvaImage, Rect, Transformer } from 'react-konva';
import FloorPlanShape from './shape-node';
import { sortShapesByZ, TRANSFORMABLE_TYPES } from './constants';
import { getShapesBounds } from './export';

function useFloorPlanImage(url) {
  const [image, setImage] = useState(null);
  useEffect(() => {
    if (!url) {
      setImage(null);
      return;
    }
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setImage(img);
    img.onerror = () => setImage(null);
    img.src = url;
  }, [url]);
  return image;
}

/**
 * Shared Konva stage: pan (space/middle-drag), wheel zoom, optional grid, shape list.
 * Walls do not use Transformer — they use endpoint handles for length only.
 */
const FloorPlanStage = ({
  width = 800,
  height = 560,
  canvas = {},
  shapes = [],
  backgroundUrl,
  colorByOccupancy = false,
  highlightedIds = [],
  invalidSeatIds = [],
  selectedId = null,
  editable = false,
  showGrid = true,
  onSelectShape,
  onStageClick,
  onShapeDragEnd,
  onShapeTransformEnd,
  onWallEndpointDrag,
  stageRef: externalStageRef,
  fitToContent = false,
  children,
}) => {
  const localStageRef = useRef(null);
  const stageRef = externalStageRef || localStageRef;
  const transformerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [spacePressed, setSpacePressed] = useState(false);
  const [panPressed, setPanPressed] = useState(false);
  const bgImage = useFloorPlanImage(backgroundUrl);
  const fittedKeyRef = useRef('');

  const canvasWidth = canvas.width || 2000;
  const canvasHeight = canvas.height || 1400;
  const gridSize = canvas.gridSize || 20;

  const selectedShape = useMemo(
    () => shapes.find((s) => s.id === selectedId) || null,
    [shapes, selectedId]
  );
  const useTransformer = editable
    && selectedShape
    && TRANSFORMABLE_TYPES.has(selectedShape.elementType);

  // Fit viewport to shape bounds (viewer) — runs when shape set / size changes
  useEffect(() => {
    if (!fitToContent || !width || !height) return;
    const key = `${shapes.length}:${width}x${height}:${shapes.map((s) => s.id).join(',')}`;
    if (fittedKeyRef.current === key) return;
    const bounds = getShapesBounds(shapes, 48);
    if (!bounds) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      fittedKeyRef.current = key;
      return;
    }
    const pad = 16;
    const sx = (width - pad * 2) / bounds.width;
    const sy = (height - pad * 2) / bounds.height;
    const nextScale = Math.min(2.5, Math.max(0.2, Math.min(sx, sy)));
    setScale(nextScale);
    setPosition({
      x: pad - bounds.x * nextScale + ((width - pad * 2) - bounds.width * nextScale) / 2,
      y: pad - bounds.y * nextScale + ((height - pad * 2) - bounds.height * nextScale) / 2,
    });
    fittedKeyRef.current = key;
  }, [fitToContent, shapes, width, height]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space' && !e.repeat && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        setSpacePressed(true);
      }
    };
    const onKeyUp = (e) => {
      if (e.code === 'Space') setSpacePressed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  useEffect(() => {
    const transformer = transformerRef.current;
    const stage = stageRef.current;
    if (!transformer || !stage) return;
    if (!useTransformer) {
      transformer.nodes([]);
      transformer.getLayer()?.batchDraw();
      return;
    }
    const byName = stage.findOne((n) => n.name() === selectedId);
    const target = byName;
    if (target && target.getClassName() !== 'Stage' && target.getClassName() !== 'Layer') {
      transformer.nodes([target]);
      transformer.getLayer()?.batchDraw();
    } else {
      transformer.nodes([]);
    }
  }, [selectedId, shapes, useTransformer, stageRef]);

  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    const scaleBy = 1.12;
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const newScale = Math.min(4, Math.max(0.25, direction > 0 ? oldScale * scaleBy : oldScale / scaleBy));
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };
    setScale(newScale);
    setPosition({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    });
  }, [stageRef]);

  const gridLines = [];
  if (showGrid) {
    for (let x = 0; x <= canvasWidth; x += gridSize) {
      gridLines.push(
        <Line key={`vx${x}`} points={[x, 0, x, canvasHeight]} stroke="#d1d5db" strokeWidth={x % (gridSize * 5) === 0 ? 1.5 : 1} listening={false} />
      );
    }
    for (let y = 0; y <= canvasHeight; y += gridSize) {
      gridLines.push(
        <Line key={`hy${y}`} points={[0, y, canvasWidth, y]} stroke="#d1d5db" strokeWidth={y % (gridSize * 5) === 0 ? 1.5 : 1} listening={false} />
      );
    }
  }

  const highlightedSet = new Set(highlightedIds);
  const invalidSeatSet = new Set(invalidSeatIds);

  return (
    <div className="relative w-full overflow-hidden bg-gray-50" style={{ height }}>
      <Stage
        ref={stageRef}
        width={width}
        height={height}
        scaleX={scale}
        scaleY={scale}
        x={position.x}
        y={position.y}
        draggable={spacePressed || panPressed}
        onWheel={handleWheel}
        onMouseDown={(e) => {
          if (e.evt.button === 1) {
            e.evt.preventDefault();
            setPanPressed(true);
          }
          if (e.target === e.target.getStage()) {
            onStageClick?.(e);
          }
        }}
        onMouseUp={(e) => {
          if (e.evt.button === 1) setPanPressed(false);
        }}
        onMouseLeave={() => setPanPressed(false)}
        onDragEnd={(e) => {
          if (e.target === stageRef.current) {
            setPosition({ x: e.target.x(), y: e.target.y() });
          }
        }}
        style={{ cursor: spacePressed || panPressed ? 'grab' : 'default' }}
      >
        <Layer>
          <Rect x={0} y={0} width={canvasWidth} height={canvasHeight} fill="#ffffff" listening={false} />
          {bgImage && (
            <KonvaImage
              image={bgImage}
              width={canvasWidth}
              height={canvasHeight}
              opacity={0.55}
              listening={false}
            />
          )}
          {gridLines}
        </Layer>
        <Layer>
          {sortShapesByZ(shapes).map((shape) => (
            <FloorPlanShape
              key={shape.id}
              shape={shape}
              colorByOccupancy={colorByOccupancy}
              highlighted={highlightedSet.has(shape.id)}
              invalid={invalidSeatSet.has(shape.id)}
              selected={selectedId === shape.id}
              draggable={editable}
              listening={!spacePressed}
              onSelect={onSelectShape}
              onDragEnd={onShapeDragEnd}
              onTransformEnd={onShapeTransformEnd}
              onWallEndpointDrag={onWallEndpointDrag}
            />
          ))}
          {editable && (
            <Transformer
              ref={transformerRef}
              rotateEnabled
              enabledAnchors={
                selectedShape?.elementType === 'door'
                  ? ['top-left', 'top-right', 'bottom-left', 'bottom-right']
                  : undefined
              }
              boundBoxFunc={(oldBox, newBox) => {
                if (newBox.width < 12 || newBox.height < 12) return oldBox;
                return newBox;
              }}
              anchorSize={10}
              anchorCornerRadius={4}
              padding={4}
            />
          )}
          {children}
        </Layer>
      </Stage>
      <div className="absolute bottom-2 right-2 bg-white/90 px-2 py-0.5 text-[10px] tracking-wide text-gray-400">
        {Math.round(scale * 100)}% · Space or middle-drag · Scroll zoom
      </div>
    </div>
  );
};

export default FloorPlanStage;
